import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, of, switchMap, finalize } from "rxjs";
import { BidStatus } from "../../../interfaces/enums";
import { BidRoundService } from "../../../services/bid-round.service";
import { BidService } from "../../../services/bids.service";
import { ScreenSizeService } from "../../../services/screen-size.service";
import { WebsocketService } from "../../../services/websocket.service";
import { EditbidComponent } from "../editbid/editbid.component";

import { TokenDetailsComponent } from "../shared/token-details/token-details.component"; // Import TokenDetailsComponent
import { PaymentService } from "src/app/services/payment/user-wallet.service";

@Component({
  selector: "app-strike-price",
  templateUrl: "./strike-price.component.html",
  styleUrls: ["./strike-price.component.css"],
  // Removed standalone: true and imports array
})
export class StrikePriceComponent implements OnInit, OnDestroy {
  bids: any[] = [];
  error: string | null = null;
  activeBids: any[] = [];
  strikeForm: FormGroup;
  strike: boolean = false;
  activeBid: any = null;
  bidRound: any = [];
  strikePrice: number = 0;
  private wsSubscription: Subscription | undefined;
  private bidRoundId: string | undefined;
  endAllocation: string = "";
  isMobile: boolean = false;
  isLoading: boolean = false;
  userBidAction: boolean = false;
  isFinalizingUpdate: boolean = false;
  finalizeMessage = "";
  loadingMessage = "";
  // Removed showTokenDetails property

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private ws: WebsocketService,
    private bidRoundService: BidRoundService,
    private bidService: BidService,
    private activatedRoute: ActivatedRoute,
    private screenSizeService: ScreenSizeService,
    private paymentService: PaymentService,
    public dialog: MatDialog
  ) {
    this.strikeForm = this.fb.group({});
  }

  ngOnInit(): void {
    const state = history.state;
    if (state && state.paymentResult === "success" && state.editContext) {
      console.log(state.editContext);
      this.isFinalizingUpdate = true; // Show a loading message
      this.finalizeMessage = "Finalizing your bid update… please wait";
      const editData = state.editContext;

      this.router.navigate([], { state: {}, replaceUrl: true });

      // Finalize the bid update
      this.bidService
        .updateBid({
          bid_id: editData.bid_id,
          bid_round_id: editData.bid_round_id,
          energy_amount: editData.energy_amount,
          bid_price: editData.bid_price,
        })
        .subscribe({
          next: () => {
            this.isFinalizingUpdate = false;
            this.getBidById(editData.bid_id); // Refresh the page data
          },
          error: (err) => {
            this.isFinalizingUpdate = false;
            this.error = "Failed to finalize bid update after payment.";
            console.error(err);
          },
        });
    } else if (
      state &&
      state.paymentResult === "success" &&
      state.acceptanceContext
    ) {
      this.isLoading = true;
      this.loadingMessage = "Payment successful. Finalizing bid acceptance...";
      this.router.navigate([], { state: {}, replaceUrl: true });

      this.finalizeAcceptance(
        state.acceptanceContext.bid_id,
        state.acceptanceContext.bid_round_id
      );
    } else {
      const bidId = this.activatedRoute.snapshot.paramMap.get("id");
      if (bidId) {
        this.getBidById(bidId);
      } else {
        this.loadBids();
      }
    }

    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  ngOnDestroy(): void {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }
    this.ws.closeBidRoundConnection(); // Close specific bid round connection
    this.bidRoundId = undefined;
  }

  navigateTo(path: string) {
    this.router.navigate([path]);
  }
  getBidById(bidId: string) {
    this.bidService.getBidById(bidId).subscribe(
      (data) => {
        this.activeBid = data;
        console.log("Bid-detail", this.activeBid);
        if (
          this.activeBid.status == BidStatus.Accepted ||
          this.activeBid.status == BidStatus.Rejected
        ) {
          this.navigateTo(`/allocation/${bidId}`);
        }
        this.getBidRound();
      },
      (error) => {
        console.error("Error", error);
        this.error = "Error loading bid: " + error.message;
      }
    );
  }

  loadBids(): void {
    const filter = { limit: 100 };
    this.bidService.getFilteredBids(filter).subscribe(
      (data) => {
        if (Array.isArray(data.items)) {
          let sortedItems = data.items.sort((a: any, b: any) => {
            return (
              new Date(b.created_on).getTime() -
              new Date(a.created_on).getTime()
            );
          });

          if (sortedItems.length == 0) {
            this.navigateTo("/");
          }

          this.activeBid = sortedItems[0];
          console.log("active bid details: ", this.activeBid);
          if (
            this.activeBid.status == BidStatus.Accepted ||
            this.activeBid.status == BidStatus.Rejected
          ) {
            this.navigateTo(`/allocation/${this.activeBid.id}`);
          }

          this.getBidRound();
        } else {
          console.error("Data is not an array:", data);
          this.error = "Unexpected data format received from server";
        }
      },
      (error) => {
        console.error("Error loading bids:", error);
        this.error = "Error loading bids: " + error.message;
      }
    );
  }

  private connectToWebSocket() {
    // Clean up any existing subscription first
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }

    if (!this.activeBid?.bid_round) {
      console.warn("Cannot connect to WebSocket: missing bid round ID");
      return;
    }

    this.bidRoundId = this.activeBid.bid_round;
    console.log(`Connecting to WebSocket for bid round: ${this.bidRoundId}`);

    // Connect using the specific bid round method
    this.ws.connectBidRound(this.bidRoundId);

    // Subscribe to the bid-round specific message stream
    this.wsSubscription = this.ws.bidRoundMessages$.subscribe({
      next: (message: any) => {
        console.log("WebSocket message received (Bid Round):", message);
        if (message.type === "strike_price") {
          this.strikePrice = parseFloat(message.strike_price);
          this.strike = true;
          this.getBidRound(); // Refresh to get latest state including strike price
        }
        if (message.type === "completed_bid_round") {
          this.navigateTo(`/allocation/${this.activeBid.id}`);
        }
      },
      error: (error) => {
        console.error("WebSocket error (Bid Round):", error);
      },
      complete: () => {
        console.log("WebSocket connection closed gracefully (Bid Round)");
      },
    });
  }

  openEditBidModal() {
    const dialogRef = this.dialog.open(EditbidComponent, {
      data: {
        active_bid: this.activeBid,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      // This will now refresh after a direct update (refund or no-change)
      if (result === "success") {
        this.getBidById(this.activeBid.id);
      }
    });
  }

  // acceptBid(accept: boolean = true) {
  //   this.userBidAction = accept;
  //   this.isLoading = true;
  //   const bidData = {
  //     bid_round_id: this.activeBid.bid_round,
  //     bid_id: this.activeBid.id,
  //     accept: accept,
  //   };
  //   this.bidService
  //     .acceptBid(bidData)
  //     .subscribe(
  //       (data) => {
  //         if (data) {
  //           this.navigateTo("/allocation" + "/" + this.activeBid.id);
  //         }
  //       },
  //       (error) => {
  //         console.error("Error loading bid rounds:", error);
  //         this.error = "Error loading bid rounds: " + error.message;
  //       }
  //     )
  //     .add(() => {
  //       this.isLoading = false;
  //     });
  // }

  handleAcceptance(accept: boolean) {
    if (!accept) {
      this.finalizeAcceptance(
        this.activeBid.id,
        this.activeBid.bid_round,
        false
      );
      return;
    }

    this.isLoading = true;
    this.loadingMessage = "Validating final settlement amount...";
    const transactionTypeId = this.activeBid.transaction_type === "Buy" ? 1 : 2;

    // Validate balance against the final strike price
    console.log(this.strikePrice);
    this.paymentService
      .validateBalance({
        bid_price: this.strikePrice, // Use the final strike price
        energy_amount: this.activeBid.energy_amount,
        transaction_type_id: transactionTypeId,
        bid_id: this.activeBid.id,
      })
      .pipe(
        switchMap((validationResponse) => {
          if (
            !validationResponse ||
            typeof validationResponse.required_balance === "undefined"
          ) {
            throw new Error(
              "Invalid response from balance validation service."
            );
          }
          const required = parseFloat(validationResponse.required_balance);
          console.log(validationResponse);
          if (required > 0) {
            this.loadingMessage = "Creating payment intent...";
            return this.paymentService.addTokens({
              amount: required,
              payment_method: "icici_cbdc",
              device: "web",
            });
          } else {
            return of({ no_payment_needed: true });
          }
        })
      )
      .subscribe({
        next: (response: any) => {
          console.log(response);
          if (response.payment_intent) {
            const paymentContext = {
              intentLink: response.payment_intent.intent_link,
              txId: response.payment_intent.transaction_id,
              amount: response.payment_intent.amount,
              message: response.payment_intent.message,
                qr_code: response.qr_code,
              returnRoute: `/strike/${this.activeBid.id}`, // Return here after payment
              acceptanceContext: {
                // Send context needed for final acceptance
                bid_id: this.activeBid.id,
                bid_round_id: this.activeBid.bid_round,
              },
              display: { title: "Final Bid Settlement" },
            };
            sessionStorage.setItem(
              "pendingPaymentContext",
              JSON.stringify(paymentContext)
            );
            this.router.navigate(["/payment"], { state: paymentContext });
          } else {
            this.finalizeAcceptance(
              this.activeBid.id,
              this.activeBid.bid_round
            );
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.error =
            err?.error?.detail || "An error occurred during final validation.";
        },
      });
  }

  finalizeAcceptance(
    bidId: string,
    bidRoundId: string,
    accept: boolean = true
  ) {
    this.isLoading = true;
    this.loadingMessage = accept ? "Accepting bid..." : "Rejecting bid...";
    this.userBidAction = accept ? true : false;

    this.bidService
      .acceptBid({ bid_id: bidId, bid_round_id: bidRoundId, accept: accept })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          if (data) {
            this.navigateTo(`/allocation/${bidId}`);
          }
        },
        error: (err) => {
          this.error = `Failed to ${accept ? "accept" : "reject"} bid.`;
        },
      });
  }

  withdrawBid() {
    const bidId = this.activeBid.id;
    this.bidService
      .withdrawBid(bidId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (resp) => {
          if (resp) {
            this.navigateTo(`/allocation/${this.activeBid.id}`);
          }
        },
        error: (err) => {
          console.error("Error withdrawing your bid:", err);
          this.error = "Error withdrawing your bid";
        },
      });
  }

  getBidRound() {
    this.bidRoundService.getBidRound(this.activeBid.bid_round).subscribe(
      (data) => {
        if (data) {
          this.bidRound = data;
          let allocations = this.bidRound.allocation_window.split(" to ");
          let endDate = allocations[1];
          this.endAllocation = endDate;
          this.strike = data.strike_price == null ? false : true;
          this.strikePrice = data.strike_price;
          this.connectToWebSocket();
          if (this.strikePrice == 0) {
            this.navigateTo(`/allocation/${this.activeBid.id}`);
          }
        }
      },
      (error) => {
        console.error("Error loading bid rounds:", error);
        this.error = "Error loading bid rounds: " + error.message;
      }
    );
  }

  // Removed toggleTokenDetails method

  openTokenDetailsDialog(): void {
    if (!this.activeBid) return; // Ensure activeBid is available

    this.dialog.open(TokenDetailsComponent, {
      data: {
        userId: this.activeBid.user_id,
        bidRoundId: this.activeBid.bid_round,
      },
      width: "600px", // Adjust width as needed
      // Add any other dialog configuration options here
    });
  }
}
